import { Alert, Modal, ModalBody } from '@execonline-inc/execonline-ui';
import { observer } from 'mobx-react';
import * as React from 'react';
import { T } from '../../../../../../Translations';
import SuccessCheckIcon from '../../../../../Cutesies/SuccessCheckIcon';
import WarningIcon from '../../../../../Cutesies/WarningIcon';
import CloneProgress from './CloneProgress';

type Props =
  | { kind: 'cloning'; collectionName: string }
  | {
      kind: 'cloned';
      collectionName: string;
      coursesNeedReview: boolean;
      onClose: () => void;
      onEdit: () => void;
      onPublishAndInvite: () => void;
    }
  | { kind: 'clone-failed'; onClose: () => void };

const content = (props: Props): React.ReactNode => {
  switch (props.kind) {
    case 'cloning':
      return <CloneProgress collectionName={props.collectionName} />;
    case 'cloned':
      return (
        <div className="w-full pb-2 pt-4 text-center">
          <SuccessCheckIcon />

          <h1 className="text-2xl font-semibold text-foreground mb-2">
            <T kind="Collection cloned" />
          </h1>
          <div className="text-muted-foreground text-sm mb-8">
            <T
              kind="A copy of <name/> is ready. It's saved as a draft until you publish it."
              name={<span className="font-semibold text-primary">{props.collectionName}</span>}
            />
          </div>

          {props.coursesNeedReview && (
            <Alert status="warning" variant="outlined" icon radius="md" className="mb-6">
              <T kind="Some courses from the original collection couldn't be copied automatically. Review this collection's courses before publishing." />
            </Alert>
          )}

          <div className="space-y-3 mt-8 flex flex-col items-center">
            <button
              className="w-full bg-primary text-primary-foreground py-2.5 px-4 rounded-md font-medium text-sm"
              onClick={props.onPublishAndInvite}
              data-intercom-target="clone-collection-publish-invite-button"
            >
              <T kind="Publish & invite participants" />
            </button>
            <button
              className="w-full bg-white border-2 border-primary text-primary py-2.5 px-4 rounded-md font-medium text-sm"
              onClick={props.onEdit}
              data-intercom-target="clone-collection-edit-button"
            >
              <T kind="Edit collection" />
            </button>
            <button
              className="text-primary transition-colors font-medium py-2 text-sm"
              onClick={props.onClose}
              data-intercom-target="clone-collection-close-button"
            >
              <T kind="Close" />
            </button>
          </div>
        </div>
      );
    case 'clone-failed':
      return (
        <div className="w-full pb-2 pt-4 text-center">
          <WarningIcon />

          <h1 className="text-2xl font-semibold text-foreground mb-2">
            <T kind="Clone failed" />
          </h1>
          <div className="text-muted-foreground text-sm mb-8">
            <T kind="Unable to clone this collection. Please try again later." />
          </div>

          <button
            className="w-full bg-primary text-primary-foreground py-2.5 px-4 rounded-md font-medium text-sm"
            onClick={props.onClose}
            data-intercom-target="clone-collection-failed-done-button"
          >
            <T kind="Done" />
          </button>
        </div>
      );
  }
};

// A single Modal instance hosts every state so the cloning -> result switch
// swaps body content in place instead of unmounting one modal and mounting
// another (no backdrop flash or chrome jump). Dismissal is disabled while
// cloning: leaving mid-clone would silently drop the completion signal, and
// closing the tab can lose the clone entirely.
function CloneModal(props: Props) {
  const dismissible = props.kind !== 'cloning';

  return (
    <Modal
      open
      escapeKey={dismissible}
      outsidePress={dismissible}
      onClose={props.kind === 'cloning' ? undefined : props.onClose}
      classNames={{ closeButton: dismissible ? '' : 'hidden' }}
    >
      {/* min-h matches the tallest state (the confirmation) so the card
          keeps one footprint while its content swaps; shorter states center
          vertically inside it. */}
      <ModalBody>
        <div className="flex min-h-[520px] w-full flex-col items-center justify-center">
          {content(props)}
        </div>
      </ModalBody>
    </Modal>
  );
}

export default observer(CloneModal);
